import { IconButton, MenuItem } from '@mui/material';
import { Popover } from '@mui/material';
import React, { useEffect } from 'react';
import classNames from 'classnames';
import { MenuOptionsType } from 'src/components/recruiterPositions/RecruiterCandidateCardFooter';
import { PositionDocument } from 'types';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { useMutation, useQueryClient } from 'react-query';
import { closeDrivePosition } from '../../apis/drive';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from 'src/context/ToastProvider';
import CloseRecruiterPositionConfirmationModal from 'src/components/recruiterPositions/CloseRecruiterPositionConfirmationModal';
import { useUserContext } from 'src/context/UserProvider';

const DrivePositionThreeDotMenu = ({ position }: { position: PositionDocument }) => {
	const { isAccountManagerLoggedIn } = useUserContext();
	const navigate = useNavigate();
	const toast = useToast();
	const queryClient = useQueryClient();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const id = open ? 'simple-popover' : undefined;
	const [isCloseRecruiterPositionConfirmationModalOpen, setIsCloseRecruiterPositionConfirmationModalOpen] =
		React.useState(false);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const [menuOptions, setMenuOptions] = React.useState<MenuOptionsType[]>([]);

	const handleClose = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(null);
	};

	const closeDrivePositionFn = useMutation(['closeDrivePosition'], closeDrivePosition, {
		onSuccess: async (data: any) => {
			queryClient.refetchQueries({ queryKey: ['getDrivePositions'], exact: false });
			setIsCloseRecruiterPositionConfirmationModalOpen(false);
		},
		onError(error: Error) {
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	useEffect(() => {
		const options: MenuOptionsType[] = [
			{
				key: 'close-position',
				label: 'Close Position',
				hide: false,
				onClick: () => {
					setIsCloseRecruiterPositionConfirmationModalOpen(true);
				},
			},
			{
				key: 'edit-position',
				label: 'Edit Position',
				hide: false,
				onClick: () => {
					navigate(
						isAccountManagerLoggedIn
							? `/edit-drive-position/${position.company._id}/${position._id}`
							: `/edit-drive-position/${position._id}`,
					);
				},
			},
		];
		setMenuOptions(options);
	}, [position]);

	return (
		<>
			<IconButton onClick={handleClick} className="[&_span]:!p-0 !p-0 border-[#E5E7EB] whitespace-pre !text-black">
				<BsThreeDotsVertical className={classNames('px-[8px]')} size={38} />
			</IconButton>

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				sx={{
					'&>.MuiPaper-root': {
						borderRadius: '8px',
						background: 'white',
						boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
					},
					'&>.MuiPaper-root>.MuiButtonBase-root': {
						minHeight: '40px',
					},
				}}
			>
				{menuOptions &&
					menuOptions
						.filter(el => !el.hide)
						.map((menuOption: MenuOptionsType, index: number) => (
							<MenuItem
								key={index}
								className={'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50]'}
								style={{
									padding: '8px 16px',
								}}
								onClick={event => {
									if (menuOption.onClick) {
										menuOption.onClick(event);
									}
									handleClose(event);
								}}
							>
								{menuOption.label}
							</MenuItem>
						))}
			</Popover>

			<CloseRecruiterPositionConfirmationModal
				isLoading={closeDrivePositionFn.isLoading}
				isOpen={isCloseRecruiterPositionConfirmationModalOpen}
				onClose={() => setIsCloseRecruiterPositionConfirmationModalOpen(false)}
				onConfirm={() => {
					closeDrivePositionFn.mutate({
						positionId: position?._id || '',
						reason: 'Position closed by recruiter or Account Manager',
					});
				}}
			/>
		</>
	);
};

export default DrivePositionThreeDotMenu;
